import { ChartingColors, Color } from '@persona-id/design-tokens';
import { ComponentProps, FC, useCallback, useContext, useMemo, useState } from 'react';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Payload } from 'recharts/types/component/DefaultLegendContent';
import { AxisInterval } from 'recharts/types/util/types';
import styled, { ThemeContext } from 'styled-components';

import ChartLayout, {
  ChartTooltipContainer,
  ChartTooltipHeader,
  ChartTooltipLabel,
  ChartTooltipLabelContainer,
} from 'dashboard/layouts/ChartLayout';
import { Period, UnixTime } from 'dashboard/lib/unixTime';
import { fillChartData } from 'dashboard/lib/utils/charting';
import {
  formatChartDateHour,
  formatChartDateLong,
  formatChartDateLongWithDay,
  formatChartDateMonth,
  formatNumber,
  formatNumberLarge,
} from 'dashboard/lib/utils/formatters';
import { ChartLegendContent } from 'dashboard/views/DashboardInquiryAnalyticsFunnelV2View/ChartLegend';
import { DEFAULT_PERCENTAGE_TICKS } from 'dashboard/views/DashboardInquiryAnalyticsFunnelV2View/FunnelConversionBarChart';
import { TransitionDuration } from 'design-system/variables/transitions';
import Dot from 'images/dashboard/dot.svg';
import useToggle from 'lib/hooks/useToggle';

export type MetricWithTimestamp = {
  created_timestamp: number;
} & { [key in string]: number };

export type TimestampToPercentages = {
  [key in number]: {
    [key in string]: {
      numerator: number;
      denominator: number;
    };
  };
};

interface FunnelConversionOverTimeLineChartProps {
  data: MetricWithTimestamp[];
  percentagesData: TimestampToPercentages;
  stepIds: string[];
  domain: [UnixTime, UnixTime];
  period: Period;
  interval: AxisInterval;
  xFormatter: (value: any, index: number) => string;
  seriesFormatter: (id: string) => string;
  includeZero?: boolean;
}

const buildZeroValue = (stepIds: string[]) => {
  const initialValue: Record<string, number> = {};
  return stepIds.reduce((acc, key) => {
    acc[key] = 0;
    return acc;
  }, initialValue);
};

const LINE_DASH_MAPPING: Record<string, string> = {};

const DEFAULT_DIMENSION_MARGIN = 16;

const DIMENSIONS = {
  height: 320,
  margin: {
    top: DEFAULT_DIMENSION_MARGIN * 2,
    left: -DEFAULT_DIMENSION_MARGIN,
    right: DEFAULT_DIMENSION_MARGIN,
  },
};

const StyledChartTooltipLabelContainer = styled(ChartTooltipLabelContainer)`
  grid-template-columns: 6fr 1fr 1fr;
  min-width: 100px;
`;

const formatPercentagesData = (
  label: number,
  dataKey: string,
  percentagesData: TimestampToPercentages,
) => {
  const labelData = percentagesData[label];
  if (labelData == null) {
    return null;
  }
  const labelKeyData = labelData[dataKey];
  if (labelKeyData == null) {
    return null;
  }
  return `${formatNumber(labelKeyData.numerator)}/${formatNumber(labelKeyData.denominator)}`;
};

/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
const TooltipContent: FC<
  ComponentProps<typeof Tooltip> &
    Pick<FunnelConversionOverTimeLineChartProps, 'period'> & {
      hoveredKey: string | null;
      labelFormatter: (string: string) => string;
      includeZero?: boolean;
      percentagesData: TimestampToPercentages;
    }
> = ({
  active,
  payload,
  label,
  period,
  hoveredKey,
  labelFormatter,
  includeZero,
  percentagesData,
}) => {
  if (!active || payload == null) {
    return null;
  }
  const labels = payload
    .slice()
    .map(
      ({ color, dataKey, value }) =>
        typeof value === 'number' &&
        (value > 0 || includeZero) && (
          <StyledChartTooltipLabelContainer
            key={dataKey}
            active={hoveredKey === dataKey}
            hasActive={hoveredKey != null}
          >
            <ChartTooltipLabel>
              <Dot style={{ color }} /> {labelFormatter(String(dataKey))}
            </ChartTooltipLabel>
            <ChartTooltipLabel>
              {formatPercentagesData(label, dataKey as string, percentagesData)}
            </ChartTooltipLabel>
            <ChartTooltipLabel>{formatNumber(+value)}%</ChartTooltipLabel>
          </StyledChartTooltipLabelContainer>
        ),
    )
    .filter(Boolean);
  return (
    labels.length > 0 && (
      <ChartTooltipContainer>
        <ChartTooltipHeader>
          {period === 'week'
            ? `Week of ${formatChartDateLong(label)}`
            : period === 'month'
              ? formatChartDateMonth(label)
              : period === 'day'
                ? formatChartDateLongWithDay(label)
                : formatChartDateHour(label)}
        </ChartTooltipHeader>
        {labels}
      </ChartTooltipContainer>
    )
  );
};

const FunnelConversionOverTimeLineChart = ({
  data,
  domain,
  percentagesData,
  period,
  seriesFormatter,
  interval,
  xFormatter,
  stepIds,
}: FunnelConversionOverTimeLineChartProps) => {
  const zeroValue = buildZeroValue(stepIds);
  const filledData = useMemo(
    () => fillChartData(data, zeroValue, domain, period, true, true),
    [data, domain, period, zeroValue],
  );
  const theme = useContext(ThemeContext);

  const [filteredPercentiles, setFilteredPercentiles] = useState<string[]>([]);
  const [legendHovered, { enable: setLegendHovered, disable: clearLegendHovered }] = useToggle();
  const [hoveredKey, setHoveredKey] = useState<string | null>(null);

  const handleLegendClick = useCallback(
    ({ dataKey }: Payload) =>
      typeof dataKey === 'string' &&
      setFilteredPercentiles(
        filteredPercentiles.includes(dataKey)
          ? filteredPercentiles.filter((s) => s !== dataKey)
          : filteredPercentiles.concat(dataKey),
      ),
    [filteredPercentiles],
  );

  const handleLegendMouseEnter = useCallback(({ dataKey }: Payload) => {
    setLegendHovered();
    typeof dataKey === 'string' && setHoveredKey(dataKey);
  }, []);

  const handleLegendMouseLeave = useCallback(() => {
    clearLegendHovered();
    setHoveredKey(null);
  }, []);

  const series = Object.keys(filledData[0]).filter((value) => stepIds.includes(value));
  const dataEmpty =
    filledData.find((v) =>
      Object.entries(v).find(([index, value]) => {
        return index !== 'created_timestamp' && value > 0;
      }),
    ) == null;
  return (
    <ChartLayout>
      <ResponsiveContainer height={DIMENSIONS.height}>
        <LineChart data={filledData} margin={DIMENSIONS.margin}>
          <CartesianGrid vertical={false} stroke={theme.color.border.secondary} />
          <XAxis
            // Note we are using categorical x axis; this is because D3 doesn't handle time series bar charts well
            dataKey='created_timestamp'
            axisLine={{ stroke: 'none' }}
            tickLine={{ stroke: 'none' }}
            tickFormatter={xFormatter}
            interval={interval}
            tick={{
              fontSize: theme.text.paragraph.mdBold.fontSize,
              fill: theme.color.text.tertiary,
            }}
          />
          <YAxis
            axisLine={{ stroke: 'none' }}
            tickLine={{ stroke: 'none' }}
            tickFormatter={formatNumberLarge}
            unit='%'
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            ticks={dataEmpty ? DEFAULT_PERCENTAGE_TICKS : null}
            tick={{
              fontSize: theme.text.paragraph.mdBold.fontSize,
              fill: theme.color.text.tertiary,
            }}
            allowDecimals={false}
          />
          <Tooltip
            isAnimationActive={false}
            content={
              <TooltipContent
                includeZero={false}
                period={period}
                hoveredKey={hoveredKey}
                labelFormatter={seriesFormatter}
                percentagesData={percentagesData}
              />
            }
          />
          {
            <Legend
              layout='vertical'
              verticalAlign='top'
              align='right'
              formatter={seriesFormatter}
              iconType='circle'
              inactiveColor={Color.CoreSlate50}
              // eslint-disable-next-line rulesdir/jsx-click-tracking-ids -- this element doesn't take ID
              onClick={handleLegendClick}
              onMouseEnter={handleLegendMouseEnter}
              onMouseLeave={handleLegendMouseLeave}
              content={<ChartLegendContent height={290} width={250} layout='vertical' />}
            />
          }
          {series.map((calc: string, idx: number) => (
            <Line
              key={calc}
              dataKey={calc}
              type='linear'
              dot={false}
              onMouseEnter={() => setHoveredKey(calc)}
              onMouseLeave={() => setHoveredKey(null)}
              stroke={ChartingColors.Categorical[idx % ChartingColors.Categorical.length]}
              strokeOpacity={!legendHovered || hoveredKey == null || hoveredKey === calc ? 1 : 0.3}
              strokeDasharray={LINE_DASH_MAPPING[calc]}
              hide={filteredPercentiles.length > 0 && !filteredPercentiles.includes(calc)}
              style={{
                transition: `fill-opacity ${TransitionDuration.Fast}, stroke ${TransitionDuration.Fast}`,
              }}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </ChartLayout>
  );
};

export default FunnelConversionOverTimeLineChart;
